import React from 'react';
import { useGameStore } from '../../store/gameStore';

interface Props {
  send: (action: string, data?: object) => void;
}

export function SettingsScreen({ send }: Props) {
  const { 
    settings, 
    updateSettings,
    setPhase,
  } = useGameStore();

  const handleUpdateSettings = (newSettings: Partial<typeof settings>) => {
    updateSettings(newSettings);
    send('updateSettings', { settings: { ...settings, ...newSettings } });
  };

  const handleContinue = () => {
    setPhase('lobby');
  };

  return (
    <div className="screen">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 className="title-pixel" style={{ fontSize: 24, marginBottom: 16 }}>
          🍞 Game Settings
        </h1>
        <p className="subtitle">Configure your restaurant adventure</p>
        <p style={{ 
          color: 'var(--ltc-text-muted)', 
          fontSize: 13,
          marginTop: 8,
        }}>
          Players can find and join your game from the Browse menu
        </p>
      </div>

      {/* Settings Card */}
      <div className="card" style={{ width: '100%', maxWidth: 400 }}>
        {/* Day Duration */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ 
            display: 'block', 
            fontWeight: 600,
            marginBottom: 12,
            color: 'var(--ltc-text-primary)',
          }}>
            ⏱️ Day Duration
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[30, 45, 60].map((duration) => (
              <button
                key={duration}
                className={`btn ${settings.dayDuration === duration ? 'btn-primary' : 'btn-secondary'}`}
                style={{ flex: 1, padding: '12px 8px' }}
                onClick={() => handleUpdateSettings({ dayDuration: duration })}
              >
                {duration} min
              </button>
            ))}
          </div>
        </div>

        {/* Difficulty */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ 
            display: 'block', 
            fontWeight: 600,
            marginBottom: 12,
            color: 'var(--ltc-text-primary)',
          }}>
            🎯 Difficulty
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['easy', 'normal', 'hard'] as const).map((diff) => (
              <button
                key={diff}
                className={`btn ${settings.difficulty === diff ? 'btn-primary' : 'btn-secondary'}`}
                style={{ flex: 1, padding: '12px 8px', textTransform: 'capitalize' }}
                onClick={() => handleUpdateSettings({ difficulty: diff })}
              >
                {diff === 'easy' && '🌱 '}
                {diff === 'normal' && '🌿 '}
                {diff === 'hard' && '🌶️ '}
                {diff}
              </button>
            ))}
          </div>
        </div>

        {/* Max Players */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ 
            display: 'block', 
            fontWeight: 600,
            marginBottom: 12,
            color: 'var(--ltc-text-primary)',
          }}>
            👥 Max Players
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[4, 6, 8].map((max) => (
              <button
                key={max}
                className={`btn ${settings.maxPlayers === max ? 'btn-primary' : 'btn-secondary'}`}
                style={{ flex: 1, padding: '12px 8px' }}
                onClick={() => handleUpdateSettings({ maxPlayers: max })}
              >
                {max} players
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <button
        className="btn btn-accent btn-large"
        style={{ marginTop: 32, minWidth: 200 }}
        onClick={handleContinue}
      >
        Continue to Lobby →
      </button>

      {/* Info */}
      <p style={{ 
        marginTop: 24, 
        color: 'var(--ltc-text-muted)',
        fontSize: 14,
        textAlign: 'center',
      }}>
        You can change these settings until the game starts
      </p>
    </div>
  );
}
